import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { http, formatApiError } from "../../lib/api";
import { SiretRegistryBar } from "../SiretRegistry";
import { Modal, F, inp, btnPrimary, btnGhost, iconBtn, useList, Table, Empty, frDate, safeDelete } from "./common";

const FIELDS = [["contact_name", "Contact"], ["phone", "Téléphone"], ["email", "E-mail"], ["orders_email", "E-mail des commandes (si différent)"],
  ["vat_number", "N° TVA"], ["website", "Site web"], ["iban", "IBAN"], ["payment_terms", "Conditions de paiement"], ["customer_number", "Notre n° client chez lui"]];

const SupplierForm = ({ value, onClose, onSaved }) => {
  const [v, setV] = useState({ name: "", siret: "", vat_number: "", address: "", postal_code: "", city: "", country: "France", contact_name: "", email: "", orders_email: "", phone: "", website: "", iban: "", payment_terms: "", customer_number: "", notes: "", ...value });
  const save = async () => {
    try { if (v.id) await http.put(`/equipment/suppliers/${v.id}`, v); else await http.post("/equipment/suppliers", v); toast.success("Fournisseur enregistré"); onSaved(); onClose(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const f = (k) => ({ value: v[k] || "", onChange: (e) => setV({ ...v, [k]: e.target.value }), className: inp, "data-testid": `supplier-${k}` });
  return (
    <Modal wide title={v.id ? "Modifier le fournisseur" : "Nouveau fournisseur"} onClose={onClose} testid="supplier-form">
      <div className="grid grid-cols-2 gap-3">
        <F label="SIRET"><input {...f("siret")} placeholder="14 chiffres — remplit la fiche automatiquement" /></F>
        <F label="Raison sociale"><input {...f("name")} /></F>
        <div className="col-span-2 -mt-2"><SiretRegistryBar v={v} setV={setV} /></div>
        <F label="Adresse" className="col-span-2"><input {...f("address")} /></F>
        <F label="Code postal"><input {...f("postal_code")} /></F>
        <F label="Ville"><input {...f("city")} /></F>
        {FIELDS.map(([k, l]) => <F key={k} label={l}><input {...f(k)} /></F>)}
        <F label="Notes" className="col-span-2"><textarea rows={2} {...f("notes")} /></F>
      </div>
      <div className="flex justify-end gap-2"><button className={btnGhost} onClick={onClose}>Annuler</button><button className={btnPrimary} disabled={!v.name.trim()} onClick={save} data-testid="supplier-save">Enregistrer</button></div>
    </Modal>
  );
};

const Registry = ({ r }) => {
  if (!r || r.status === "unchecked") return <span className="text-xs text-slate-400">Non vérifié</span>;
  if (r.status === "active") return <span className="text-xs text-emerald-700 inline-flex items-center gap-1" data-testid="supplier-registry-active"><CheckCircle2 className="h-3.5 w-3.5" />Actif · {frDate(r.checked_at)}</span>;
  const l = { closed: "Établissement fermé", not_found: "SIRET introuvable", error: "Annuaire indisponible" }[r.status];
  return <span className="text-xs text-rose-700 inline-flex items-center gap-1" data-testid="supplier-registry-warn"><AlertTriangle className="h-3.5 w-3.5" />{l}</span>;
};

export const SupplierList = () => {
  const [rows, load] = useList("/equipment/suppliers");
  const [form, setForm] = useState(null);
  const check = async (s) => { try { await http.post(`/equipment/suppliers/${s.id}/registry-check`); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const del = async (s) => { if (!window.confirm(`Retirer ${s.name} des fournisseurs ?`)) return; safeDelete(`/equipment/suppliers/${s.id}`, load); };
  return (
    <div className="space-y-3" data-testid="supplier-list">
      <div className="flex"><button className={`${btnPrimary} ml-auto`} onClick={() => setForm({})} data-testid="supplier-add"><Plus className="h-4 w-4" />Nouveau fournisseur</button></div>
      <Table head={["Fournisseur", "SIRET", "Contact", "E-mail commandes", "Annuaire", ""]} testid="supplier-table">
        {rows.length === 0 && <tr><td colSpan={6}><Empty>Aucun fournisseur</Empty></td></tr>}
        {rows.map((s) => (
          <tr key={s.id} data-testid={`supplier-row-${s.id}`}>
            <td className="px-3 py-2"><b>{s.name}</b><div className="text-xs text-slate-500">{s.address} {s.postal_code} {s.city}</div></td>
            <td className="px-3 py-2 font-mono text-xs">{s.siret || "—"}</td>
            <td className="px-3 py-2 text-xs">{s.contact_name}<div>{s.phone}</div></td>
            <td className="px-3 py-2 text-xs">{s.orders_email || s.email || <span className="text-amber-700">À renseigner</span>}</td>
            <td className="px-3 py-2"><Registry r={s.registry} /></td>
            <td className="px-3 py-2 text-right whitespace-nowrap">
              <button className={iconBtn} title="Revérifier le SIRET" onClick={() => check(s)} data-testid={`supplier-check-${s.id}`}><RefreshCw className="h-4 w-4" /></button>
              <button className={iconBtn} onClick={() => setForm(s)} data-testid={`supplier-edit-${s.id}`}><Pencil className="h-4 w-4" /></button>
              <button className={`${iconBtn} text-rose-600`} onClick={() => del(s)} data-testid={`supplier-del-${s.id}`}><Trash2 className="h-4 w-4" /></button>
            </td>
          </tr>
        ))}
      </Table>
      {form && <SupplierForm value={form} onClose={() => setForm(null)} onSaved={load} />}
    </div>
  );
};
